import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogDescription,
} from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useBrandManager } from "@/hooks/useBrandManager";
import { usePressReleases } from "@/hooks/usePressReleases";
import { usePressReleasePrompts } from "@/hooks/usePressReleasePrompts";
import { usePressReleaseCreate } from "@/contexts/PressReleaseCreateContext";
import { toast } from "sonner";
import { PressReleaseSourceType } from "@/types/pressReleases";

export const GlobalPressReleaseCreateDialog = () => {
  const { isOpen, closeDialog } = usePressReleaseCreate();
  const { currentBrandId } = useBrandManager();
  const { createPressRelease, generatePressRelease } = usePressReleases(currentBrandId);
  const { prompts, isLoading: isLoadingPrompts } = usePressReleasePrompts(currentBrandId);

  const [inputType, setInputType] = useState<PressReleaseSourceType>('text');
  const [urlInput, setUrlInput] = useState('');
  const [textInput, setTextInput] = useState('');
  const [fileInput, setFileInput] = useState<File | null>(null);
  const [selectedPromptId, setSelectedPromptId] = useState<string>('');
  const [pressReleaseName, setPressReleaseName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const uploadFileToStorage = async (file: File): Promise<string | null> => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user || !currentBrandId) {
        throw new Error('User not authenticated or no brand selected');
      }

      const fileName = `${user.user.id}/${Date.now()}-${file.name}`;
      
      const { data, error } = await supabase.storage
        .from('press-release-sources')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Error uploading file:', error);
        return null;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('press-release-sources')
        .getPublicUrl(data.path);

      return publicUrl;
    } catch (error) {
      console.error('Error in uploadFileToStorage:', error);
      return null;
    }
  };

  const resetForm = () => {
    setPressReleaseName('');
    setUrlInput('');
    setTextInput('');
    setFileInput(null);
    setSelectedPromptId('');
    setInputType('text');
  };

  const handleSubmit = async () => {
    if (!currentBrandId) {
      toast.error('Please select a brand first');
      return;
    }

    if (!selectedPromptId) {
      toast.error('Please select a prompt');
      return;
    }

    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      toast.error('Please sign in first');
      return;
    }

    let sourceData: any = {};

    switch (inputType) {
      case 'url':
        if (!urlInput.trim()) {
          toast.error('Please enter a URL');
          return;
        }
        sourceData = { url: urlInput.trim() };
        break;
      case 'text':
        if (!textInput.trim()) {
          toast.error('Please enter some text');
          return;
        }
        sourceData = { text: textInput.trim() };
        break;
      case 'file':
        if (!fileInput) {
          toast.error('Please select a file');
          return;
        }
        
        const fileUrl = await uploadFileToStorage(fileInput);
        if (!fileUrl) {
          toast.error('Failed to upload file');
          return;
        }
        
        sourceData = {
          fileName: fileInput.name,
          fileUrl: fileUrl,
          fileSize: fileInput.size,
          fileType: fileInput.type
        };
        break;
    }

    setIsGenerating(true);

    try {
      const pressRelease = await createPressRelease({
        brand_id: currentBrandId,
        user_id: user.user.id,
        name: pressReleaseName || `Press Release - ${new Date().toLocaleDateString()}`,
        source_type: inputType,
        source_data: sourceData,
        prompt_id: selectedPromptId,
      });

      if (!pressRelease) {
        throw new Error('Failed to create press release');
      }

      toast.success('Press release created, generating content...');

      const success = await generatePressRelease(pressRelease.id);

      if (success) {
        resetForm();
        closeDialog();
      }
    } catch (error) {
      console.error('Error creating press release:', error);
      toast.error('Failed to create press release');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeDialog();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitleWithBug 
            popupName="Create Press Release" 
            popupContext="/press-releases" 
            isOpen={isOpen}
          >
            Create Press Release
          </DialogTitleWithBug>
          <DialogDescription>
            Generate a professional press release from your content
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Press Release Name */}
          <div className="space-y-2">
            <Label htmlFor="pr-name">Press Release Name *</Label>
            <Input
              id="pr-name"
              placeholder="e.g., Q4 Product Launch Announcement"
              value={pressReleaseName}
              onChange={(e) => setPressReleaseName(e.target.value)}
            />
          </div>

          {/* Source Type Selection */}
          <div className="space-y-2">
            <Label>Source Type</Label>
            <Select value={inputType} onValueChange={(value) => setInputType(value as PressReleaseSourceType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="url">URL</SelectItem>
                <SelectItem value="file">File</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Source Input */}
          {inputType === 'url' && (
            <div className="space-y-2">
              <Label htmlFor="url-input">URL</Label>
              <Input
                id="url-input"
                type="url"
                placeholder="https://example.com/article"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
              />
            </div>
          )}

          {inputType === 'text' && (
            <div className="space-y-2">
              <Label htmlFor="text-input">Text Content</Label>
              <Textarea
                id="text-input"
                placeholder="Enter or paste your content here..."
                className="min-h-[120px]"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
              />
            </div>
          )}

          {inputType === 'file' && (
            <div className="space-y-2">
              <Label htmlFor="file-input">Upload File</Label>
              <Input
                id="file-input"
                type="file"
                accept=".txt,.pdf,.doc,.docx"
                onChange={(e) => setFileInput(e.target.files?.[0] || null)}
              />
              {fileInput && (
                <p className="text-sm text-muted-foreground truncate">
                  Selected: {fileInput.name} ({(fileInput.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>
          )}

          {/* Prompt Selection */}
          <div className="space-y-2">
            <Label>Prompt Template</Label>
            <Select value={selectedPromptId} onValueChange={setSelectedPromptId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a prompt template" />
              </SelectTrigger>
              <SelectContent>
                {isLoadingPrompts ? (
                  <SelectItem value="loading" disabled>Loading prompts...</SelectItem>
                ) : prompts.length === 0 ? (
                  <SelectItem value="none" disabled>No prompts available - create one first</SelectItem>
                ) : (
                  prompts.map(prompt => (
                    <SelectItem key={prompt.id} value={prompt.id}>
                      {prompt.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={closeDialog} disabled={isGenerating}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isGenerating || !selectedPromptId || !pressReleaseName.trim()}
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Generate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
